import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from '../core/guards/auth.guard';
import { DashboardComponent } from './submission-data/dashboard/dashboard.component';
import { SubmitComponent } from './submission-data/submit/submit.component';
import { EfileReturnReviewComponent } from './components/return-review/efile-return-review.component';
import { EfileScrubbingComponent } from './components/efile-scrubbing/efile-scrubbing.component';
import { EfileSetupComponent } from './components/efile-setup/efile-setup.component';
import { PdfUploadComponent } from './components/pdf-upload/pdf-upload.component';
import { EfileKeyXmlGenComponent } from './components/key-xml-gen/efile-key-xml-gen.component';
import { EfileElementsDataComponent } from './components/efile-elements-data/efile-elements-data.component';
import { SubmissionDataComponent } from './submission-data/submission-data.component';
import { MigrationHistoryComponent } from './components/migration-history/migration-history.component';
import { EfileOverridesComponent } from './components/overrides/efile-overrides.component';
import { XmlConsolUploadComponent } from './components/xml-consol-upload/xml-consol-upload.component';
import { DeleteFormComponent } from './components/delete-form/delete-form.component';
import { LockUnlockComponent } from './components/lock-unlock/lock-unlock.component';
import { XmlSyncComponent } from './components/xml-sync/xml-sync.component';
import { AmendSupersededReturnsComponent } from './components/amend-superseded-returns/amend-superseded-returns.component';
import { EfileAddEditComponent } from './components/add-edit/add-edit.component';
import { EfileXmlCompareComponent } from './components/xml-compare/efile-xml-compare.component';

const routes: Routes = [
  { component: SubmitComponent, path: 'submit', canActivate: [AuthGuard] },
  { component: DashboardComponent, path: 'dashboard', canActivate: [AuthGuard] },
  // Option C container routes. These replace the legacy ui-router states:
  //   /return-review    -> submissions-dasboard.submission.return-review
  //   /efile-scrubbing  -> submissions-dasboard.submission.efileScrubbing
  //   /setup            -> enabledDisabled / unlockValidation / formElementsReport (merged shell)
  { component: EfileReturnReviewComponent, path: 'return-review', canActivate: [AuthGuard] },
  { component: EfileScrubbingComponent, path: 'efile-scrubbing', canActivate: [AuthGuard] },
  { component: EfileSetupComponent, path: 'setup', canActivate: [AuthGuard] },
  // Phase 2 leaf-component routes:
  //   /pdf-upload    -> submissions-dasboard.submission.pdfUpload (full port)
  //   /key-xml-gen   -> submissions-dasboard.submission.keyXmlGen (STUB shell)
  //   /efile-data    -> submissions-dasboard.submission.data      (reuses existing Path-B stub)
  { component: PdfUploadComponent, path: 'pdf-upload', canActivate: [AuthGuard] },
  { component: EfileKeyXmlGenComponent, path: 'key-xml-gen', canActivate: [AuthGuard] },
  { component: EfileElementsDataComponent, path: 'efile-data', canActivate: [AuthGuard] },
  // submissions-dasboard.submission.overrides (efile.js L163)
  { component: EfileOverridesComponent, path: 'overrides', canActivate: [AuthGuard] },

  // ---------------------------------------------------------------------------
  // Phase 3 leaf-component routes — legacy context-action screens reached from
  // grid actions / dashboard buttons. Components are already declared in
  // EfileModule; Phase 3 just wires routes + deep-link parity.
  //
  //   /xml-upload                -> submissions-dasboard.submission.xmlUploadKeyId       (efile.js L156)
  //   /delete-form               -> submissions-dasboard.submission.deleteForm            (efile.js L172)
  //   /lock-unlock               -> submissions-dasboard.submission.lockUnlock            (efile.js L179)
  //   /xml-sync                  -> submissions-dasboard.submission.xmlSync               (efile.js L193)
  //   /amendsuperseded-returns   -> submissions-dasboard.submission.amendSupersededReturns (efile.js L214)
  //   /add-edit                  -> submissions-dasboard.submission.data.addEdit          (efile.js L149)
  // ---------------------------------------------------------------------------
  { component: XmlConsolUploadComponent, path: 'xml-upload', canActivate: [AuthGuard] },
  { component: DeleteFormComponent, path: 'delete-form', canActivate: [AuthGuard] },
  { component: LockUnlockComponent, path: 'lock-unlock', canActivate: [AuthGuard] },
  { component: XmlSyncComponent, path: 'xml-sync', canActivate: [AuthGuard] },
  { component: AmendSupersededReturnsComponent, path: 'amendsuperseded-returns', canActivate: [AuthGuard] },
  { component: EfileAddEditComponent, path: 'add-edit', canActivate: [AuthGuard] },
  { component: EfileXmlCompareComponent, path: 'xml-compare', canActivate: [AuthGuard] },

  // ---------------------------------------------------------------------------
  // Legacy efile state URLs (ui/app/components/efile/efile.js)
  // These were top-level (NOT workspace.*) ui-router states:
  //   submissions-dasboard            -> /submissions-dasboard
  //   submissions-dasboard.list       -> /submissions-dasboard/list
  //   submissions-dasboard.submission -> /submissions-dasboard/submission/:id
  //   + nested children (/dashboard, /submit, /data, ...)
  //   migration-hist (DST) preserved as top-level path for parity.
  // They render directly via SubmissionDataComponent / MigrationHistoryComponent
  // (no `workspace/` wrapper, no WorkspaceScreenComponent CE lookup).
  // ---------------------------------------------------------------------------
  { path: 'submissions-dasboard', redirectTo: 'submissions-dasboard/list', pathMatch: 'full' },
  { component: SubmissionDataComponent, path: 'submissions-dasboard/list', canActivate: [AuthGuard] },
  { component: SubmissionDataComponent, path: 'submissions-dasboard/submission/:id', canActivate: [AuthGuard] },
  { component: SubmissionDataComponent, path: 'submissions-dasboard/submission/:id/:subState', canActivate: [AuthGuard] },
  // Legacy nested state `submissions-dasboard.submission.data.addEdit`
  // (efile.js L149) → URL `/submission/:id/data/add-edit`. The first
  // segment after `:id` is the parent state ("data") and the second is
  // the actual child sub-state. SubmissionDataComponent reads both via
  // ActivatedRoute.url to drive the breadcrumb + ngSwitch.
  { component: SubmissionDataComponent, path: 'submissions-dasboard/submission/:id/:parentState/:subState', canActivate: [AuthGuard] },

  // Alias with the correctly-spelled "dashboard" segment. Kept in lock-step
  // with the "dasboard" variant so either URL reaches the same component.
  { path: 'submissions-dashboard', redirectTo: 'submissions-dasboard/list', pathMatch: 'full' },
  { path: 'submissions-dashboard/list', redirectTo: 'submissions-dasboard/list', pathMatch: 'full' },
  { path: 'submissions-dashboard/submission/:id', redirectTo: 'submissions-dasboard/submission/:id', pathMatch: 'full' },
  { path: 'submissions-dashboard/submission/:id/:subState', redirectTo: 'submissions-dasboard/submission/:id/:subState' },
  { path: 'submissions-dashboard/submission/:id/:parentState/:subState', redirectTo: 'submissions-dasboard/submission/:id/:parentState/:subState' },

  { component: MigrationHistoryComponent, path: 'migration-hist', canActivate: [AuthGuard] },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class EfileRoutingModule { }
